import { useState, useEffect, useRef } from "react";
import { useGoogleMapsScript } from "./useGoogleMapsScript";

interface UsePlacesAutocompleteOptions {
	inputRef?: React.RefObject<HTMLInputElement>;
	types?: string[];
	fields?: string[];
	componentRestrictions?: { country: string };
}

const DEFAULT_FIELDS = [
	"place_id",
	"formatted_address",
	"name",
	"types",
	"geometry",
	"address_components",
];

export function usePlacesAutocomplete({
	inputRef,
	types,
	fields = DEFAULT_FIELDS,
	componentRestrictions,
}: UsePlacesAutocompleteOptions): {
	place: GooglePlaceResult | null;
	isLoaded: boolean;
} {
	const { isLoaded } = useGoogleMapsScript();
	const [place, setPlace] = useState<GooglePlaceResult | null>(null);
	const instanceRef = useRef<InstanceType<
		typeof window.google.maps.places.Autocomplete
	> | null>(null);

	useEffect(() => {
		if (!isLoaded || !inputRef?.current) return;

		try {
			const instance = new window.google.maps.places.Autocomplete(
				inputRef.current,
				{ types, fields, componentRestrictions },
			);
			instance.addListener("place_changed", () =>
				setPlace(instance.getPlace()),
			);
			instanceRef.current = instance;
		} catch {
			// Autocomplete init failed (e.g. API key missing or DOM not ready)
		}

		return () => {
			if (instanceRef.current && window.google?.maps?.event) {
				window.google.maps.event?.clearInstanceListeners(instanceRef.current);
				instanceRef.current = null;
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoaded]);

	return { place, isLoaded };
}
